import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  UploadCloud, FileCheck2, ShoppingCart, X, SlidersHorizontal,
  ChevronDown, Package, AlertTriangle, ArrowRight,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { DropOffChecklist } from "@/components/DropOffChecklist";
import { uploadArtwork, money, bulkPercent } from "@/lib/api";
import { BUILDER_CONFIGS, LEGACY_BUILDERS, RUSH_TIERS } from "@/data/builderConfigs";

const ACCENT_CLS = {
  magenta: { text: "text-brand-magenta", border: "border-brand-magenta", soft: "bg-brand-magenta/10" },
  cyan: { text: "text-brand-cyan", border: "border-brand-cyan", soft: "bg-brand-cyan/10" },
  yellow: { text: "text-brand-yellow", border: "border-brand-yellow", soft: "bg-brand-yellow/10" },
};

const initSelections = (cfg) => {
  const init = { ...(cfg.defaults || {}) };
  for (const dd of cfg.dropdowns) {
    if (dd.fields) {
      dd.fields.forEach((f) => { init[f.id] = init[f.id] || ""; });
      continue;
    }
    const opts = dd.dynamicOptions
      ? dd.dynamicOptions(init)
      : dd.groups
        ? dd.groups[0].options
        : dd.options;
    init[dd.id] = init[dd.id] || opts[0]?.id || null;
  }
  return init;
};

const OptionTile = ({ selected, onClick, testid, children, ac }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`border px-4 py-3 text-left transition-colors ${
      selected ? `${ac.border} ${ac.soft}` : "border-white/15 hover:border-white/40"
    }`}
  >
    {children}
  </button>
);

const DeltaNote = ({ delta, ac }) =>
  delta ? (
    <span className={`font-mono text-[10px] ${ac.text}`}>+{money(delta)}</span>
  ) : (
    <span className="font-mono text-[10px] text-zinc-500">base</span>
  );

// Low-profile, collapsed-by-default drop-off checklist — keeps the Live Quote
// and Add to Cart high on mobile. Full block renders on the confirmation screen.
const DropOffNotice = ({ sneaker }) => {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="dropoff-notice" className="border border-white/10 bg-ink-2">
      <button
        type="button"
        data-testid="dropoff-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2.5 font-mono text-xs text-brand-yellow underline-offset-4 hover:underline">
          <Package className="h-4 w-4 shrink-0" />
          {sneaker
            ? "Using your own sneakers? View our quick 3-step drop-off checklist"
            : "Using your own garment? View our quick 3-step drop-off checklist"}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div data-testid="dropoff-panel" className="border-t border-white/10 px-4 py-4">
          <DropOffChecklist sneaker={sneaker} />
        </div>
      )}
    </section>
  );
};

const GarmentNotice = () => (
  <section data-testid="garment-notice" className="border border-brand-magenta/50 bg-brand-magenta/5 px-5 py-4">
    <p className="flex gap-3 text-sm leading-relaxed text-zinc-200">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-brand-magenta" />
      <span>
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-brand-magenta">
          ⚠️ Customer Notice:
        </span>{" "}
        All graphics and logos submitted must be completely print-ready, high-resolution (300 DPI
        minimum), with transparent backgrounds (.PNG or vector format). TLF DESIGNZ is not
        responsible for low-quality or blurry prints resulting from low-resolution file submissions.
      </span>
    </p>
  </section>
);

const METHOD_GUIDE = [
  {
    title: "Choose DTF (Direct to Film)",
    body: "Best for multi-color designs, photos, and high-vibrancy graphics on dark or light garments (cotton, polyester, or blends).",
  },
  {
    title: "Choose Sublimation",
    body: "Best for lightweight white or light-colored polyester shirts. The ink fuses directly into the fabric for a completely seamless, zero-feel finish that will never crack or peel.",
  },
  {
    title: "Choose HTV / Vinyl",
    body: "Best for bold, clean text layouts, simple solid-color logos, jerseys, or adding eye-catching special finishes like metallic glitter and holographic foil.",
  },
  {
    title: "Choose Embroidery",
    body: "Best for premium corporate wear, polo collars, structured hats, and high-density stitched branding.",
  },
];

const MethodGuide = () => {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="method-guide" className="border border-brand-cyan/40 bg-ink-2">
      <button
        type="button"
        data-testid="method-guide-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <span className="font-display text-base font-bold uppercase tracking-wide text-brand-cyan">
          Which Print Method is Right For Me?
        </span>
        <ChevronDown className={`h-5 w-5 text-brand-cyan transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4 border-t border-white/10 px-5 py-4"
        >
          {METHOD_GUIDE.map((m) => (
            <div key={m.title}>
              <p className="font-display text-sm font-bold uppercase tracking-wide text-white">{m.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-zinc-400">{m.body}</p>
            </div>
          ))}
        </motion.div>
      )}
    </section>
  );
};

const Configurator = () => {
  const { slug, type } = useParams();
  const raw = type || slug;
  const key = BUILDER_CONFIGS[raw] ? raw : LEGACY_BUILDERS[raw];
  const cfg = BUILDER_CONFIGS[key];
  const { addItem, setOpen } = useCart();
  const navigate = useNavigate();
  const fileInput = useRef(null);

  const [selections, setSelections] = useState(() => (cfg ? initSelections(cfg) : {}));
  const [qty, setQty] = useState(1);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [artwork, setArtwork] = useState(null);
  const [rush, setRush] = useState(false);

  useEffect(() => {
    if (!cfg) return;
    setSelections(initSelections(cfg));
    setQty(1);
    setArtwork(null);
    setRush(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!cfg || Object.keys(selections).length === 0) return;
    let changed = false;
    const next = { ...selections };
    for (const dd of cfg.dropdowns) {
      if (!dd.dynamicOptions) continue;
      const opts = dd.dynamicOptions(next);
      if (!opts.some((o) => o.id === next[dd.id])) {
        next[dd.id] = opts[0].id;
        changed = true;
      }
    }
    if (changed) setSelections(next);
  }, [selections, cfg]);

  const getOptions = (dd) => {
    if (dd.dynamicOptions) return dd.dynamicOptions(selections);
    if (dd.groups) return dd.groups.flatMap((g) => g.options);
    return dd.options;
  };

  const pricing = useMemo(() => {
    if (!cfg || (cfg.dropdowns.length > 0 && Object.keys(selections).length === 0)) return null;
    if (cfg.compute) {
      const r = cfg.compute(selections, qty);
      const units = r.units ?? qty;
      return { ...r, invalid: false, pct: bulkPercent(cfg.categorySlug, units) };
    }
    let unit = cfg.base || 0;
    const parts = [];
    for (const dd of cfg.dropdowns) {
      if (dd.fields) continue;
      const opt = getOptions(dd).find((o) => o.id === selections[dd.id]);
      if (!opt) return null;
      if (opt.base) unit = opt.base;
      else unit += opt.delta || 0;
      parts.push(opt.label);
    }
    const q = cfg.noQty ? 1 : qty;
    return { invalid: false, unit, total: unit * q, qty: q, parts, pct: bulkPercent(cfg.categorySlug, q) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, selections, qty]);

  if (!cfg) {
    return (
      <div className="mx-auto max-w-7xl px-6 pb-32 pt-48 text-center">
        <p className="font-display text-4xl font-black uppercase text-zinc-500">Builder not found</p>
        <Link to="/" className="mt-4 inline-block font-mono text-xs uppercase tracking-widest text-brand-cyan">
          Back to home
        </Link>
      </div>
    );
  }

  const ac = ACCENT_CLS[cfg.accent] || ACCENT_CLS.cyan;
  const Icon = cfg.icon || SlidersHorizontal;
  const effQty = cfg.noQty ? (pricing?.units || 1) : qty;
  const rushTier = cfg.rush || RUSH_TIERS[cfg.categorySlug];
  const rushFee =
    rush && rushTier && pricing && !rushTier.approvalOnly
      ? rushTier.fee ?? Math.max(rushTier.min || 0, Math.round((rushTier.pct || 0) * pricing.total))
      : 0;
  const rushConfirm = Boolean(rushTier?.confirmAt && effQty >= rushTier.confirmAt);
  const tlText = (t) => (typeof t === "function" ? t(effQty) : t);
  const showDropOff = selections.sourcing === "customer" || cfg.alwaysDropOff;
  const isSneaker = cfg.categorySlug === "customized-sneakers";
  // Describing the design in words makes the artwork upload fully optional.
  const designDescribed = Boolean(String(selections.design || "").trim());

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["png", "svg", "ai", "pdf", "jpg", "jpeg"].includes(ext)) {
      toast.error("Unsupported file", { description: "Upload .PNG, .SVG, .AI or .PDF artwork" });
      return;
    }
    setUploading(true);
    try {
      const rec = await uploadArtwork(file);
      setArtwork(rec);
      toast.success("Artwork uploaded", { description: rec.original_filename });
    } catch (e) {
      toast.error("Upload failed", { description: e?.response?.data?.detail || "Try again in a moment" });
    } finally {
      setUploading(false);
    }
  };

  const addToCart = () => {
    if (!pricing || pricing.invalid) return;
    const fieldParts = (cfg.dropdowns || [])
      .filter((d) => !d.visibleIf || d.visibleIf(selections, qty))
      .flatMap((d) => d.fields || [])
      .filter((f) => selections[f.id])
      .map((f) => `${f.label}: ${selections[f.id]}`);
    const configLabel =
      [...(pricing.parts || []), ...fieldParts].join(" • ") + (rushFee ? " • ⚡ RUSH UPGRADE" : "");
    addItem({
      key: `${key}-${Object.values(selections).join("-")}-${cfg.noQty ? "pack" : qty}-${rushFee ? "rush" : "std"}-${artwork?.id || "no-art"}`,
      slug: key,
      name: cfg.title,
      unitPrice: Number(pricing.unit.toFixed(2)),
      qty: cfg.noQty ? pricing.units : qty,
      lineTotal: cfg.noQty ? pricing.total : null,
      discountBase: pricing.discountable != null ? Number(pricing.discountable.toFixed(2)) : null,
      categorySlug: cfg.categorySlug,
      configLabel,
      rushFee,
      rushLabel: rushFee ? tlText(rushTier.rush) : null,
      artworkName: artwork?.original_filename || null,
    });
    setOpen(true);
  };

  let sectionNo = 0;

  return (
    <div data-testid="configurator-page" className="mx-auto max-w-7xl px-4 pb-24 pt-40 sm:px-6">
      <header className="mb-10">
        <p className={`font-mono text-xs uppercase tracking-[0.4em] ${ac.text}`}>
          Interactive Print Builder
        </p>
        <h1 className="mt-2 flex items-center gap-4 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl">
          <Icon className={`h-10 w-10 ${ac.text} sm:h-14 sm:w-14`} strokeWidth={1.25} />
          {cfg.title}
        </h1>
        <p className="mt-2 font-mono text-xs uppercase tracking-widest text-zinc-500">{cfg.subtitle}</p>
        {cfg.notice && (
          <p data-testid="builder-notice" className="mt-4 border border-brand-cyan/30 bg-brand-cyan/5 px-4 py-3 text-sm leading-relaxed text-zinc-300">
            {cfg.notice}
          </p>
        )}
      </header>

      <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-8">
          {cfg.garment && <GarmentNotice />}
          {cfg.garment && cfg.dropdowns.some((d) => d.id === "method") && <MethodGuide />}

          {cfg.dropdowns
            .filter((dd) => !dd.visibleIf || dd.visibleIf(selections, qty))
            .map((dd) => {
            sectionNo += 1;
            const num = String(sectionNo).padStart(2, "0");
            return (
              <section key={dd.id}>
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  {num} — {dd.label}
                </p>
                {dd.fields ? (
                  <div className="grid gap-3 sm:grid-cols-2" data-testid={`builder-dropdown-${dd.id}`}>
                    {dd.fields.map((f) => (
                      <div key={f.id} className={f.wide ? "sm:col-span-2" : ""}>
                        <label
                          htmlFor={`builder-field-${f.id}`}
                          className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-zinc-500"
                        >
                          {f.label}
                        </label>
                        {f.type === "textarea" ? (
                          <textarea
                            id={`builder-field-${f.id}`}
                            data-testid={`builder-field-${f.id}`}
                            rows={3}
                            value={selections[f.id] || ""}
                            onChange={(e) => setSelections((s) => ({ ...s, [f.id]: e.target.value }))}
                            placeholder={f.placeholder}
                            className="w-full resize-y border border-white/15 bg-ink px-3.5 py-2.5 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-brand-yellow"
                          />
                        ) : (
                          <input
                            id={`builder-field-${f.id}`}
                            data-testid={`builder-field-${f.id}`}
                            type="text"
                            value={selections[f.id] || ""}
                            onChange={(e) => setSelections((s) => ({ ...s, [f.id]: e.target.value }))}
                            placeholder={f.placeholder}
                            className="w-full border border-white/15 bg-ink px-3.5 py-2.5 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-brand-yellow"
                          />
                        )}
                        {f.help && (
                          <p className="mt-1 text-[11px] leading-snug text-zinc-500">{f.help}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : dd.select ? (
                  <select
                    data-testid={`builder-select-${dd.id}`}
                    value={selections[dd.id] || ""}
                    onChange={(e) => {
                      if (e.target.value === "quote") {
                        navigate("/bulk-inquiry");
                        return;
                      }
                      setSelections((s) => ({ ...s, [dd.id]: e.target.value }));
                    }}
                    className="w-full max-w-md border border-white/15 bg-ink px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-white outline-none transition-colors focus:border-brand-yellow"
                  >
                    {getOptions(dd).map((opt) => (
                      <option key={opt.id} value={opt.id} data-testid={`builder-option-${dd.id}-${opt.id}`}>
                        {opt.label}
                      </option>
                    ))}
                    {dd.quoteOption && (
                      <option value="quote" data-testid={`builder-option-${dd.id}-quote`}>
                        51+ — Request Quote
                      </option>
                    )}
                  </select>
                ) : dd.groups ? (
                  <div className="space-y-6" data-testid={`builder-dropdown-${dd.id}`}>
                    {dd.groups.map((g) => (
                      <div key={g.heading} className="border border-white/10 bg-ink-2 p-4">
                        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                          <p className="font-display text-base font-black uppercase tracking-wide text-white">
                            {g.heading}
                          </p>
                          <p className="font-mono text-[10px] uppercase tracking-widest text-brand-yellow">
                            {g.note}
                          </p>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {g.options.map((opt) => (
                            <OptionTile
                              key={opt.id}
                              testid={`builder-option-${dd.id}-${opt.id}`}
                              selected={selections[dd.id] === opt.id}
                              onClick={() => setSelections((s) => ({ ...s, [dd.id]: opt.id }))}
                              ac={ac}
                            >
                              <span className="block font-display text-base font-bold uppercase leading-tight text-white">
                                {opt.label}
                              </span>
                              {opt.note && (
                                <span className="mt-1 block text-[11px] normal-case leading-snug text-zinc-400">
                                  {opt.note}
                                </span>
                              )}
                            </OptionTile>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    data-testid={`builder-dropdown-${dd.id}`}
                    className={`grid gap-3 ${getOptions(dd).length > 4 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}
                  >
                    {getOptions(dd).map((opt) => (
                      <OptionTile
                        key={opt.id}
                        testid={`builder-option-${dd.id}-${opt.id}`}
                        selected={selections[dd.id] === opt.id}
                        onClick={() => setSelections((s) => ({ ...s, [dd.id]: opt.id }))}
                        ac={ac}
                      >
                        <span className="block font-display text-base font-bold uppercase leading-tight text-white">
                          {opt.label}
                        </span>
                        {!cfg.compute && opt.delta !== undefined && <DeltaNote delta={opt.delta} ac={ac} />}
                        {opt.desc && (
                          <span className="mt-1 block text-[11px] leading-snug text-zinc-400">{opt.desc}</span>
                        )}
                      </OptionTile>
                    ))}
                  </div>
                )}
                {dd.helper && dd.helper(selections) && (
                  <p
                    data-testid={`builder-helper-${dd.id}`}
                    className="mt-3 border border-brand-cyan/30 bg-brand-cyan/5 px-4 py-2.5 font-mono text-xs leading-relaxed text-brand-cyan"
                  >
                    {dd.helper(selections)}
                  </p>
                )}
              </section>
            );
          })}

          {!cfg.noQty && (
            <section>
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                {String(++sectionNo).padStart(2, "0")} — {cfg.qtyLabel || "Quantity"}
              </p>
              <select
                data-testid="builder-quantity"
                value={qty}
                onChange={(e) => {
                  if (e.target.value === "quote") {
                    navigate("/bulk-inquiry");
                    return;
                  }
                  setQty(Number(e.target.value));
                }}
                className="w-full max-w-md border border-white/15 bg-ink px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-white outline-none transition-colors focus:border-brand-yellow"
              >
                {Array.from({ length: 101 - (cfg.qtyMin || 1) }, (_, i) => i + (cfg.qtyMin || 1)).map((n) => (
                  <option key={n} value={n} data-testid={`builder-quantity-option-${n}`}>
                    {n}
                  </option>
                ))}
                <option value="quote" data-testid="builder-quantity-option-quote">
                  101+ — Request a Quote
                </option>
              </select>
              {bulkPercent(cfg.categorySlug, qty) > 0 && (
                <p
                  data-testid="builder-quantity-bulk-note"
                  className="mt-2 font-mono text-[10px] font-bold uppercase tracking-wider text-brand-yellow"
                >
                  Bulk pricing applied at checkout
                </p>
              )}
            </section>
          )}

          {cfg.oversized && (
            <section className="halftone border border-brand-yellow/40 bg-ink-2 p-6" data-testid="oversized-section">
              <p className="font-display text-xl font-black uppercase tracking-wide text-white">
                Custom Bulk Oversized Labels (Over 5")
              </p>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                Oversized sticker and label runs over 5 inches are quoted per-project by our
                production team.
              </p>
              <button
                type="button"
                data-testid="oversized-quote-button"
                onClick={() => navigate("/bulk-inquiry")}
                className="mt-4 flex items-center gap-2 bg-brand-yellow px-6 py-3.5 font-display text-sm font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan"
              >
                Request a Custom Quote for Oversized Prints (5"+) <ArrowRight className="h-4 w-4" />
              </button>
            </section>
          )}

          {showDropOff && <DropOffNotice sneaker={isSneaker} />}

          <section>
            <p className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {String(++sectionNo).padStart(2, "0")} — Artwork Upload (.JPG .JPEG .PNG .PDF .SVG .AI)
              {designDescribed && !artwork && (
                <span
                  data-testid="artwork-optional-chip"
                  className="border border-emerald-400/50 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-emerald-400"
                >
                  Optional
                </span>
              )}
            </p>
            <div
              data-testid="configurator-file-dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFile(e.dataTransfer.files?.[0]);
              }}
              onClick={() => fileInput.current?.click()}
              className={`halftone flex cursor-pointer flex-col items-center justify-center border-2 border-dashed p-10 text-center transition-colors ${
                dragOver ? `${ac.border} ${ac.soft}` : "border-white/20 hover:border-white/40"
              }`}
            >
              <input
                ref={fileInput}
                type="file"
                accept=".png,.svg,.ai,.pdf,.jpg,.jpeg"
                className="hidden"
                data-testid="configurator-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              {artwork ? (
                <div className="flex items-center gap-3">
                  <FileCheck2 className={`h-8 w-8 ${ac.text}`} />
                  <div className="text-left">
                    <p data-testid="configurator-artwork-name" className="font-mono text-sm text-white">
                      {artwork.original_filename}
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                      {(artwork.size / 1024).toFixed(0)} KB — locked to this configuration
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="configurator-artwork-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setArtwork(null);
                    }}
                    className="ml-2 text-zinc-500 hover:text-brand-magenta"
                    aria-label="Remove artwork"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <UploadCloud className={`h-10 w-10 ${uploading ? "animate-pulse" : ""} ${ac.text}`} />
                  <p className="mt-3 font-display text-lg font-bold uppercase text-white">
                    {uploading ? "Uploading…" : "Drag & drop your artwork"}
                  </p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                    or click to browse • max 25MB • 300 DPI recommended
                  </p>
                </>
              )}
            </div>
            <p
              data-testid="artwork-optional-note"
              className="mt-2 font-mono text-[10px] leading-relaxed tracking-wide text-zinc-300"
            >
              No design file? No worries. Upload a rough sketch or leave blank if you described
              your idea above.
            </p>
          </section>
        </div>

        <div>
          <div className="sticky top-32 border border-white/10 bg-ink-2">
            <div className="h-1 cmyk-line" />
            <div className="p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Live Quote</p>
              {pricing && (
                <motion.div
                  key={`${Object.values(selections).join("-")}-${qty}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="mt-4 space-y-3"
                >
                  {cfg.quoteRows ? (
                    cfg.quoteRows(selections, pricing).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-3 text-sm">
                        <span className="shrink-0 text-zinc-400">{k}</span>
                        <span
                          data-testid={`quote-row-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="text-right font-mono text-xs text-white"
                        >
                          {v}
                        </span>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="flex justify-between gap-3 text-sm">
                        <span className="shrink-0 text-zinc-400">Configuration</span>
                        <span data-testid="configurator-summary-config" className="text-right font-mono text-xs text-white">
                          {(pricing.parts || []).join(" / ")}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400">Quantity</span>
                        <span className="font-mono text-xs text-white">
                          {cfg.noQty ? `${pricing.units} units` : `${qty} units`}
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-white/10 pt-3 text-sm">
                        <span className="text-zinc-400">Unit price</span>
                        <span data-testid="configurator-unit-price" className="font-mono text-sm text-white">
                          {money(pricing.unit)}
                        </span>
                      </div>
                    </>
                  )}
                  {rushFee > 0 && (
                    <div className="flex justify-between text-sm" data-testid="configurator-rush-row">
                      <span className="text-zinc-400">Rush Order Upgrade</span>
                      <span className="font-mono text-sm text-brand-yellow">+{money(rushFee)}</span>
                    </div>
                  )}
                  <div className="flex items-baseline justify-between">
                    <span className="font-display text-lg font-bold uppercase text-white">Total</span>
                    <span data-testid="configurator-total-price" className="font-display text-4xl font-black text-brand-yellow">
                      {money(pricing.total + rushFee)}
                    </span>
                  </div>
                </motion.div>
              )}
              {rushTier && (
                <div data-testid="production-timeline-block" className="mt-5 border-t border-white/10 pt-4">
                  <p
                    data-testid="production-timeline-text"
                    className={`font-mono text-xs leading-relaxed ${rush && rushFee > 0 ? "font-bold text-brand-yellow" : "text-zinc-300"}`}
                  >
                    {rush && rushFee > 0 ? tlText(rushTier.rush) : tlText(rushTier.standard)}
                  </p>
                  {rushTier.approvalOnly ? (
                    <p
                      data-testid="rush-approval-note"
                      className="mt-3 border border-white/15 px-3.5 py-3 font-mono text-[11px] leading-relaxed text-zinc-400"
                    >
                      {rushTier.approvalOnly}
                    </p>
                  ) : rushConfirm ? (
                    <p
                      data-testid="rush-confirm-note"
                      className="mt-3 border border-brand-yellow/40 bg-brand-yellow/5 px-3.5 py-3 font-mono text-[11px] leading-relaxed text-brand-yellow"
                    >
                      ⚡ Rush availability for {effQty}+ pieces must be confirmed with TLF Designz before checkout — contact us first.
                    </p>
                  ) : (
                    <label
                      data-testid="rush-toggle-label"
                      className={`mt-3 flex cursor-pointer items-start gap-3 border px-3.5 py-3 transition-colors ${
                        rush ? "border-brand-yellow/60 bg-brand-yellow/10" : "border-white/15 hover:border-white/40"
                      }`}
                    >
                      <input
                        type="checkbox"
                        data-testid="rush-toggle"
                        checked={rush}
                        onChange={(e) => setRush(e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#FFE600]"
                      />
                      <span>
                        <span className="block font-display text-sm font-bold uppercase tracking-wide text-white">
                          Rush Order Upgrade
                        </span>
                        <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-wider text-brand-yellow">
                          {rushTier.fee
                            ? `+${money(rushTier.fee)} — Jump The Production Queue`
                            : `+${Math.round((rushTier.pct || 0) * 100)}% of order (min ${money(rushTier.min || 0)}) — Jump The Production Queue`}
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              )}
              {pricing && pricing.pct > 0 && (
                <p
                  data-testid="configurator-bulk-indicator"
                  className="mt-4 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-2.5 text-center font-mono text-[10px] font-bold uppercase tracking-widest text-brand-yellow"
                >
                  Bulk pricing applied at checkout
                </p>
              )}
              <button
                data-testid="configurator-add-to-cart"
                onClick={addToCart}
                disabled={!pricing || pricing.invalid}
                className="mt-6 flex w-full items-center justify-center gap-2 bg-brand-yellow py-4 font-display text-base font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ShoppingCart className="h-4 w-4" /> Add to Cart
              </button>
              <p className="mt-3 text-center font-mono text-[9px] uppercase tracking-widest text-zinc-600">
                Free digital proof before production
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configurator;
